import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import ReligiousEducationDetails from '../components/ReligiousEducationDetails';
import { Container, Typography, CircularProgress, Alert, Button, Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLoading } from '../contexts/LoadingContext'; // Assuming you have this for consistent loading UI
import { useError } from '../contexts/ErrorContext'; // Assuming you have this for consistent error UI

const ReligiousEducationViewerPage = () => {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const [registration, setRegistration] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use global loading/error contexts if available and preferred
  const { showLoading, hideLoading } = useLoading() || { showLoading: () => {}, hideLoading: () => {} };
  const { showError: showGlobalError } = useError() || { showError: () => {} };

  const fetchRegistration = useCallback(async () => {
    setInitialLoading(true);
    setError(null);
    showLoading(); // Show global loading indicator
    try {
      const response = await axios.get(`/religious-education/${registrationId}`);
      setRegistration(response.data);
    } catch (err) {
      console.error('Error fetching registration:', err);
      setError(`Failed to load registration data. ${err.response?.data?.message || err.message}`);
      showGlobalError('Failed to load registration data.'); // Show global error
    }
    hideLoading(); // Hide global loading indicator
    setInitialLoading(false);
  }, [registrationId, showLoading, hideLoading, showGlobalError]);

  useEffect(() => {
    if (registrationId) {
      fetchRegistration();
    }
  }, [registrationId, fetchRegistration]);

  const handleUpdate = () => {
    // Re-fetch the registration data if it's updated via the Details component
    fetchRegistration();
  };

  if (initialLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchRegistration}>
            RETRY
          </Button>
        }>
          {error}
        </Alert>
        <Button 
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/religious-education')}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Container>
    );
  }

  if (!registration) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">Registration not found.</Alert>
        <Button 
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/religious-education')}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Container>
    );
  }

  // The ReligiousEducationDetails is a modal, so we render it here directly.
  // It will take over the screen as designed.
  // The onClose for this context should probably navigate back to the list.
  return (
    <Box>
        {/* Optional: Add a header or back button outside the modal if ReligiousEducationDetails doesn't provide one suitable for a page context */}    
        {/* 
        <Container sx={{pt: 2, display: 'flex', justifyContent: 'flex-start'}}>
             <Button 
                variant="outlined" 
                startIcon={<ArrowBackIcon />} 
                onClick={() => navigate('/religious-education')}
             >
                Back to Registrations List
            </Button>
        </Container>
        */}
        <ReligiousEducationDetails
            registration={registration}
            open={true} // Always open when on this page
            onClose={() => navigate('/religious-education')} // Navigate back on close
            onUpdate={handleUpdate} // Re-fetch data if updated
        />
    </Box>
  );
};

export default ReligiousEducationViewerPage; 